import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Grid, Paper, TextField } from '@mui/material';
import WestIcon from '@mui/icons-material/West';
import SearchIcon from '@mui/icons-material/Search';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate, useParams } from 'react-router';
import MainContainer from '../../../components/MainContainer';
import { headerDataMission } from '../../../data/modules/mission-header';
import Title from '../../../components/Title';
import Spacer from '../../../components/Spacer';
import LoadingSimpleBackdrop from '../../tools/LoadingSimpleBackdrop';
import ProvisionalFuelMissionService from '../../../services/ProvisionalFuelMissionService';

const formatDate = (value, includeTime = false) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const day = `${date.getDate()}`.padStart(2, '0');
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const year = date.getFullYear();
  if (!includeTime) {
    return `${day}/${month}/${year}`;
  }
  const hours = `${date.getHours()}`.padStart(2, '0');
  const minutes = `${date.getMinutes()}`.padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const resolveLineLabel = (row = {}) => {
  const dateRange = row.date_begin || row.date_end
    ? `(${formatDate(row.date_begin)} - ${formatDate(row.date_end)})`
    : '';
  if (row.type === 'DR') {
    return [dateRange, row.depart, row.arriver].filter(Boolean).join(' ');
  }
  if (row.activite === 'mis') {
    return [dateRange, row.region].filter(Boolean).join(' ');
  }
  return [dateRange, row.direction_code || row.direction_location].filter(Boolean).join(' ');
};

const resolveAction = (row = {}) => row.action || row.event || 'Modification';

const resolveUser = (row = {}) => row.name || row.user_name || '';

const FuelMissionHistory = () => {
  const { agenda_id, vehicle_id } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({
    user: '',
    debut: '',
    fin: '',
    query: '',
  });
  const [appliedFilter, setAppliedFilter] = useState({
    user: '',
    debut: '',
    fin: '',
    query: '',
  });

  useEffect(() => {
    const fetchHistory = async () => {
      if (!agenda_id) {
        return;
      }
      setLoading(true);
      try {
        const response = await ProvisionalFuelMissionService.getFuelHistory(
          agenda_id,
          vehicle_id || 0
        );
        if (response?.status) {
          const history = Array.isArray(response.data) ? response.data : [];
          setRows(
            history.map((item, index) => ({
              id: item.history_id || item.id || index,
              ...item,
            }))
          );
        } else {
          setRows([]);
        }
      } catch (error) {
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [agenda_id, vehicle_id]);

  const filteredRows = useMemo(() => {
    if (!rows.length) {
      return rows;
    }
    return rows.filter((row) => {
      if (appliedFilter.user) {
        const userValue = resolveUser(row).toLowerCase();
        if (!userValue.includes(appliedFilter.user.toLowerCase())) {
          return false;
        }
      }
      if (appliedFilter.query) {
        const lineValue = resolveLineLabel(row).toLowerCase();
        if (!lineValue.includes(appliedFilter.query.toLowerCase())) {
          return false;
        }
      }
      if (appliedFilter.debut) {
        const date = row.created_at ? new Date(row.created_at) : null;
        if (date && date < new Date(appliedFilter.debut)) {
          return false;
        }
      }
      if (appliedFilter.fin) {
        const date = row.created_at ? new Date(row.created_at) : null;
        if (date) {
          const end = new Date(appliedFilter.fin);
          end.setHours(23, 59, 59, 999);
          if (date > end) {
            return false;
          }
        }
      }
      return true;
    });
  }, [rows, appliedFilter]);

  const columns = useMemo(
    () => [
      {
        field: 'created_at',
        headerName: 'Date',
        minWidth: 160,
        flex: 0.7,
        renderCell: (params) => (
          <span>{formatDate(params.row.created_at || params.row.date, true)}</span>
        ),
      },
      {
        field: 'action',
        headerName: 'Action',
        minWidth: 220,
        flex: 0.9,
        renderCell: (params) => <span>{resolveAction(params.row)}</span>,
      },
      {
        field: 'user',
        headerName: 'Utilisateur',
        minWidth: 200,
        flex: 0.8,
        renderCell: (params) => <span>{resolveUser(params.row)}</span>,
      },
      {
        field: 'line',
        headerName: "Ligne d'agenda",
        minWidth: 260,
        flex: 1.2,
        renderCell: (params) => <span>{resolveLineLabel(params.row)}</span>,
      },
      {
        field: 'old_value',
        headerName: 'Ancienne valeur',
        minWidth: 160,
        flex: 0.7,
        renderCell: (params) => <span>{params.row.old_value ?? ''}</span>,
      },
      {
        field: 'new_value',
        headerName: 'Nouvelle valeur',
        minWidth: 160,
        flex: 0.7,
        renderCell: (params) => <span>{params.row.new_value ?? ''}</span>,
      },
    ],
    []
  );

  const handleChangeFilter = (event) => {
    const { name, value } = event.target;
    setFilter((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilter = () => {
    setAppliedFilter({ ...filter });
  };

  const handleResetFilter = () => {
    const empty = { user: '', debut: '', fin: '', query: '' };
    setFilter(empty);
    setAppliedFilter(empty);
  };

  return (
    <MainContainer {...headerDataMission}>
      <LoadingSimpleBackdrop openLoadingSimpleBackdrop={loading} />
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6} md={3}>
          Utilisateur:
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            value={filter.user}
            name="user"
            onChange={handleChangeFilter}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          Ligne:
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            value={filter.query}
            name="query"
            onChange={handleChangeFilter}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          Date debut:
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            type="date"
            value={filter.debut}
            name="debut"
            onChange={handleChangeFilter}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          Date fin:
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            type="date"
            value={filter.fin}
            name="fin"
            onChange={handleChangeFilter}
          />
        </Grid>
        <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="outlined" color="secondary" onClick={handleResetFilter}>
            Reinitialiser
          </Button>
          <Button variant="contained" color="primary" onClick={handleApplyFilter} startIcon={<SearchIcon />}>
            Filtrer
          </Button>
          <Button variant="contained" color="primary" onClick={() => navigate(-1)}>
            <WestIcon sx={{ mr: 1 }} />
            Retour
          </Button>
        </Grid>
      </Grid>
      <Spacer y={1} />
      <Paper
        elevation={0}
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
        }}
      >
        <Title>Historique des modifications - carburant previsionnel</Title>
        <Box sx={{ width: '100%' }}>
          <DataGrid
            autoHeight
            rows={filteredRows}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 20, 50]}
            disableSelectionOnClick
          />
        </Box>
      </Paper>
    </MainContainer>
  );
};

export default FuelMissionHistory;
